import {isEmpty} from "./ArrayUtils";
import {deserialize, serialize} from "./SerializationUtils";

/**
 * example how to use
 * const [searchParams, setSearchParams] = useSearchParams();
 * from react-router-dom
 * and to sync states and link
 *
 *     const [baseItem, setBaseItem] = React.useState({});
 *     const baseItemFieldsForLink = [
 *         {name: 'itemType', linkParam: 'item_type'},
 *         {name: 'itemSubType', linkParam: 'item_sub_type'},
 *         {name: 'name', linkParam: 'item'},
 *         {name: 'rarity', linkParam: 'item_rarity'},
 *         {name: 'title', linkParam: 'item_title'},
 *     ]
 *
 *     const [seller, setSeller] = React.useState({});
 *     const sellerFieldsForLink = [
 *         {name: 'username', linkParam: 'seller'},
 *     ]
 *
 *     const [searchParams, setSearchParams] = useSearchParams();
 *     const [changedSearchParams, setChangedSearchParams] = React.useState(null);
 *
 *     React.useEffect(() => {
 *         updateStatesOrLink(searchParams, setSearchParams, changedSearchParams, setChangedSearchParams, [
 *             {stateObject: seller, setStateObject: setSeller, stateObjectFields: sellerFieldsForLink},
 *             {stateObject: baseItem, setStateObject: setBaseItem, stateObjectFields: baseItemFieldsForLink},
 *         ])
 *     }, [seller, baseItem, searchParams]);
 */

export const updateStatesOrLink = function(urlSearchParams, setUrlSearchParams, changedUrlSearchParams, setChangedUrlSearchParams, states) {
    if(!shallowEqual(urlSearchParams, changedUrlSearchParams)) {
        let anyStateUpdate = false;
        states.forEach(state => {
            anyStateUpdate = anyStateUpdate || updateStateFromLinkParams(urlSearchParams, state);
        });

        if(!anyStateUpdate && changedUrlSearchParams === null) {
            //if there was no update from the link and we are here the first time (changedUrlSearchParams == null) then we update the link from the state
            updateLinkParamsFromStates(setUrlSearchParams, states)
        }

        setChangedUrlSearchParams(urlSearchParams);
    } else {
        updateLinkParamsFromStates(setUrlSearchParams, states)
    }
}

export const shallowEqual = function(objA, objB) {
    if (objA === objB) return true;
    if (
        typeof objA !== 'object' || objA === null ||
        typeof objB !== 'object' || objB === null
    ) return false;

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) return false;

    for (let key of keysA) {
        if (objA[key] !== objB[key]) return false;
    }

    return true;
}

const serializeArrayState = function(state) {
    if(!state.isStateArrayOrObject) {
        console.error("Method only supports serialization of arrays")
        return "";
    }

    return serialize(state.stateObject);
}

const deserializeArrayState = function(serialized) {
    return deserialize(serialized, []);
}

//if the state is an array or object, then we have to serialize it for the link
export const updateLinkParamsFromStates=function(setUrlSearchParams, states) {
    setUrlSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        states.forEach(state => {

            if(state.isStateArrayOrObject) {
                if(isEmpty(state.stateObject)) {
                    newParams.delete(state.linkParam);
                } else {
                    newParams.set(state.linkParam, serializeArrayState(state));
                }
            } else {
                state.stateObjectFields.forEach(field => {
                    //we update the link only if value is not default like empty string or 0
                    let isDefault = true;

                    if (state.stateObject && state.stateObject[field.name]) {
                        let value = state.stateObject[field.name];
                        if (typeof value === 'string' && value.trim() !== '') {
                            isDefault = false;
                        }

                        if (typeof value === 'number' && value !== 0) {
                            isDefault = false;
                        }

                        if (typeof value === 'boolean') {
                            isDefault = false;
                        }

                        if (typeof value === 'object' && value !== {} && value !== [] && value !== null) {
                            isDefault = false;
                        }

                        if(!isDefault) {
                            //TODO: arrays werden hier noch nicht unterstützt, aber bisher gab es nur strings, number und boolean als fields
                            newParams.set(field.linkParam, value);
                        }
                    }

                    if(isDefault) {
                        newParams.delete(field.linkParam);
                    }
                })
            }
        });
        return newParams;
    });
}

//returns true if the state got an update
//if the state is an array or object, then we have to deserialize it from the link
const updateStateFromLinkParams=function(urlSearchParams, state) {

    if(state.isStateArrayOrObject) {
        const paramFromLink = urlSearchParams.get(state.linkParam);
        const deserialized = deserializeArrayState(paramFromLink);
        state.setStateObject(deserialized);
        return deserialized;
    }

    const temp = Object.assign({}, state.stateObject);
    let updateStateObject = false;

    state.stateObjectFields.forEach(field => {
        const paramFromLink = urlSearchParams.get(field.linkParam);
        if (paramFromLink) {
            if(temp[field.name] !== paramFromLink) {
                temp[field.name] = paramFromLink;
                updateStateObject = true;
            }
        }
    })

    if(updateStateObject) {
        console.log("Updating state with temp", state, temp);
        state.setStateObject(temp);
        return true
    } else {
        return false;
    }
}